import { Component } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import lazySizes from 'lazysizes';
import { __assign } from 'tslib';

enum States {
	Preload = 'lazypreload',
	Loading = 'state-a-image--loading',
	Loaded = 'state-a-image--loaded',
}

enum Selectors {
	Image = '.js-a-image__image',
}

enum ClassNames {
	PreloadForPrint = 'js-a-image__image-print',
}

// webpack and lazySizesConfig > https://github.com/aFarkas/lazysizes/issues/410#issuecomment-318690654
if (lazySizes) {
	__assign(lazySizes.cfg, {
		preloadAfterLoad: false,
		preloadClass: States.Preload,
		loadingClass: States.Loading,
		loadedClass: States.Loaded,
		debug: false,
	});

	// load images for print event
	// adapted from https://github.com/aFarkas/lazysizes/tree/gh-pages/plugins/print
	// see also: https://www.tjvantoll.com/2012/06/15/detecting-print-requests-with-javascript/
	if (window.addEventListener) {
		const onprint = () => {
			const elements = document.getElementsByClassName(ClassNames.PreloadForPrint);

			Array.from(elements).forEach((item) => {
				lazySizes.loader.unveil(item);
			});
		};

		addEventListener('beforeprint', onprint, false);

		// fallback (e.g. safari)
		if (!(('onbeforeprint' in window) as any) && window.matchMedia) {
			const mediaQueryList = window.matchMedia('print');
			mediaQueryList.addListener((mql) => {
				if (mql.matches) {
					onprint();
				}
			});
		}
	}
}

// The @Component decorator will connect the class with `data-g-name="Image"` elements.
@Component('Image')
export class Image extends GondelJqueryComponent {
	private $image: JQuery;

	/**
	 * Returns a promise which resolves when the image finished loading
	 * or rejects if there was an error
	 */
	public getImageLoadedPromise(): Promise<any> {
		this.$image = this.$ctx.find(Selectors.Image);

		return new Promise((resolve, reject) => {
			this.checkForLoadedImage(resolve, reject);
		});
	}

	/**
	 * Event when the image has finished with loading
	 */
	private _handleImageLoaded(resolve: (value?: any) => void) {
		this.$image.off('load');
		this.$image.off('error');

		resolve();
	}

	/**
	 * Event when an error happened during loading
	 */
	private _handleImageError(reject: (value?: any) => void) {
		this.$image.off('load');
		this.$image.off('error');

		reject();
	}

	/**
	 * Check if the image has already been loaded before running the js, otherwise add load listener
	 * Inspired by https://github.com/aFarkas/lazysizes/issues/123#issuecomment-154439353
	 */
	private checkForLoadedImage(resolve: (value?: any) => void, reject: (value?: any) => void) {
		if (this.$image.length > 0) {
			// check if image has been loaded by lazysizes before running this logic
			if (this.$image.hasClass(States.Loaded)) {
				resolve();
			} else {
				this.$image.on('load', this._handleImageLoaded.bind(this, resolve));
				this.$image.on('error', this._handleImageError.bind(this, reject));
			}
		}
	}
}
